import * as React from "react";
import { IoGridOutline } from "react-icons/io5";

import { IAdvisorComparisonTableBlock } from "@types";

import { cn } from "../cn";
import { FOCUS_RING } from "../theme";
import { SeriesDot, seriesColor } from "./series";
import { DASH, numericColumns, parseAmount } from "./tableUtils";

/**
 * Shared building blocks for the S1A / S1B distribution views: the column analysis that decides
 * what's plottable, the chart data shape, the tooltip, and the table/chart toggle. The two nodes
 * differ only in how they draw it — S1B is a marker table + line chart, S1A is a card grid +
 * grouped bar chart — so everything common lives here. The palette itself lives in ./series.
 */

/** The sample-count column ("n") — kept out of the chart axis (and hidden in S1B's table). */
export function isCountColumn(header: string): boolean {
  return header.trim().toLowerCase() === "n";
}

export function isMedianColumn(header: string): boolean {
  const h = header.trim().toLowerCase();
  return h === "median" || h === "p50";
}

/** An estimated/fitted column, e.g. "P90 (est.)" — shown de-emphasised. */
export function isEstimatedColumn(header: string): boolean {
  return header.toLowerCase().includes("est");
}

/**
 * The columns forming the distribution axis: numeric, excluding the row label (col 0) and the
 * sample count. This is exactly the set the chart plots.
 */
export function axisColumns(block: IAdvisorComparisonTableBlock): number[] {
  const numeric = numericColumns(block);
  const out: number[] = [];
  block.columns.forEach((column, i) => {
    if (i !== 0 && numeric[i] && !isCountColumn(column)) out.push(i);
  });
  return out;
}

/** Eligible for a chart toggle: a real spread (>=3 value columns) across multiple rows. */
export function isChartableDistribution(
  block: IAdvisorComparisonTableBlock
): boolean {
  return axisColumns(block).length >= 3 && block.rows.length >= 2;
}

export interface ChartSeries {
  label: string;
  stroke: string;
}
export interface ChartBundle {
  series: ChartSeries[];
  data: Record<string, unknown>[];
}

/**
 * Reshape the table into chart data: one point per axis column (P25, P50, ...), each series
 * (row) contributing its parsed number to plot plus the original formatted cell under `__fmt`
 * for the tooltip. Parsing is the ONLY place the client reads through the backend's money
 * formatting, and only to plot — every visible number still comes from the backend verbatim.
 */
export function buildChart(block: IAdvisorComparisonTableBlock): ChartBundle {
  const axis = axisColumns(block);
  const series: ChartSeries[] = block.rows.map((row, r) => ({
    label: row.cells[0] ?? `Series ${r + 1}`,
    stroke: seriesColor(r).stroke,
  }));
  const data = axis.map(columnIndex => {
    const point: Record<string, unknown> = { axis: block.columns[columnIndex] };
    const fmt: Record<string, string> = {};
    block.rows.forEach((row, r) => {
      const { label } = series[r];
      point[label] = parseAmount(row.cells[columnIndex]);
      fmt[label] = row.cells[columnIndex] ?? DASH;
    });
    point.__fmt = fmt;
    return point;
  });
  return { series, data };
}

export function abbreviate(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000)
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return `${value}`;
}

// Structural props so we don't depend on recharts' version-specific TooltipProps generics.
interface TooltipEntry {
  dataKey?: string | number;
  value?: number;
  color?: string;
  payload?: Record<string, unknown>;
}

export function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  label?: string | number;
  payload?: TooltipEntry[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  // Every point carries the original formatted cells, so the tooltip speaks the backend's numbers.
  const formatted = (payload[0]?.payload?.__fmt ?? {}) as Record<
    string,
    string
  >;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1.5 font-medium text-gray-800">{label}</div>
      <div className="flex flex-col gap-1">
        {payload.map(entry => {
          const key = String(entry.dataKey);
          return (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-gray-500">
                <SeriesDot color={entry.color} />
                {key}
              </span>
              <span className="font-medium text-gray-800">
                {formatted[key] ?? entry.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded transition-colors",
        FOCUS_RING,
        active
          ? "bg-white text-gray-800 shadow-sm"
          : "text-gray-500 hover:text-gray-800"
      )}
    >
      {children}
    </button>
  );
}

export type DistributionView = "table" | "chart";

/**
 * The segmented switch between a block's detail view and its chart. The icon and the detail
 * view's label come from the caller — S1B's detail view is a table, S1A's is a card grid.
 */
export function ViewToggle({
  view,
  onChange,
  chartIcon,
  detailLabel = "Table view",
}: {
  view: DistributionView;
  onChange: (view: DistributionView) => void;
  chartIcon: React.ReactNode;
  detailLabel?: string;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md bg-gray-100 p-0.5">
      <ToggleButton
        active={view === "table"}
        onClick={() => onChange("table")}
        label={detailLabel}
      >
        <IoGridOutline className="h-4 w-4" />
      </ToggleButton>
      <ToggleButton
        active={view === "chart"}
        onClick={() => onChange("chart")}
        label="Chart view"
      >
        {chartIcon}
      </ToggleButton>
    </div>
  );
}
